import { Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DestinationOption } from '@/types/provider-destinations';

import { CHANNEL_ICONS, CHANNEL_LABELS, formatLastSuccess } from './utils';
import { VerificationBadge } from './VerificationBadge';

interface DestinationOptionCardProps {
  option: DestinationOption;
  onSelect: (option: DestinationOption) => void;
  isRecommended?: boolean;
  className?: string;
}

export const DestinationOptionCard = ({
  option,
  onSelect,
  isRecommended,
  className,
}: DestinationOptionCardProps) => {
  const ChannelIcon = CHANNEL_ICONS[option.channel_type];

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border p-3',
        isRecommended && 'border-blue-200 bg-blue-50/50',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-zinc-100">
          <ChannelIcon className="size-4 text-zinc-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isRecommended && (
              <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-400" />
            )}
            <span className="truncate text-sm font-medium text-zinc-900">
              {option.value}
            </span>
            <VerificationBadge status={option.verification_status} />
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>{CHANNEL_LABELS[option.channel_type]}</span>
            {option.confidence_score != null && (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>{Math.round(option.confidence_score * 100)}% conf.</span>
              </>
            )}
            {option.last_success_at !== undefined && (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>{formatLastSuccess(option.last_success_at)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onSelect(option)}
      >
        Use
      </Button>
    </div>
  );
};
